/**
 * useOnboardingRedirect Hook
 * Checks if user needs onboarding and redirects accordingly
 */

import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'
import { useOnboarding } from '@/contexts/OnboardingContext.context'
import { workspaceService } from '@/services'

/**
 * Hook to automatically redirect users to onboarding if they have no data
 * Should be used on protected pages that new users land on
 */
export function useOnboardingRedirect() {
  const { user } = useAuth()
  const { isOnboardingComplete, startOnboarding } = useOnboarding()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const checkOnboarding = async () => {
      // Skip if already on onboarding page
      if (location.pathname === '/onboarding') return

      // Skip if onboarding is already complete
      if (isOnboardingComplete) return

      // Skip if no user
      if (!user) return

      try {
        // Check if user has any workspaces
        const workspaces = await workspaceService.getWorkspacesForUser(user.id)

        // If no workspaces, start onboarding
        if (workspaces.length === 0) {
          startOnboarding()
          navigate('/onboarding', { replace: true })
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error)
      }
    }

    checkOnboarding()
  }, [user, isOnboardingComplete, location.pathname, startOnboarding, navigate])
}
